import { Head, useForm, usePage } from "@inertiajs/react";
import ContactFormField from "@/components/marketing/contact-form-field";
import CtaBand from "@/components/marketing/cta-band";
import PageHero from "@/components/marketing/page-hero";
import SectionHeading from "@/components/marketing/section-heading";
import SmartLink from "@/components/marketing/smart-link";
import TeamGrid from "@/components/marketing/team-grid";
import PublicLayout from "@/layouts/public-layout";
import { companyProfile, siteLinks } from "@/data/marketing-site";

const meetingAgenda = [
  "Om jeres situation primært handler om NIS2, DORA, kundekrav eller et behov for bedre governance.",
  "Om det rigtige næste skridt er Obsero Core, Obsero Enterprise eller et skarpere scope først.",
  "Hvilke roller, beslutninger og materialer der bør være med i den videre proces.",
  "Hvordan et realistisk forløb mod compliance i drift kan se ud hos jer."
];

const contactOptions = {
  Demo: "Book en demo",
  Core: "Obsero Core",
  Enterprise: "Obsero Enterprise",
  NIS2: "NIS2",
  DORA: "DORA",
  Governance: "Governance",
  Risiko: "Risikostyring",
  Incident: "ITSM & Incident",
  Leverandoerer: "Leverandørstyring",
  Andet: "Andet"
};

function Contact() {
  const { props } = usePage();
  const contactForm = props?.contact_form || {};
  const flash = props?.flash || {};
  const initialValues = contactForm.values || {};
  const interestOptions = contactForm.interest_options || Object.keys(contactOptions);
  const serverErrors = contactForm.errors || {};
  const { data, setData, post, processing, errors, transform } = useForm({
    name: initialValues.name || "",
    email: initialValues.email || "",
    phone: initialValues.phone || "",
    organization: initialValues.organization || "",
    interest: initialValues.interest || interestOptions[0] || "Demo",
    message: initialValues.message || ""
  });

  return (
    <>
      <Head title="Kontakt">
        <meta
          head-key="description"
          name="description"
          content="Book et uforpligtende møde med Obsero og få et klart billede af vejen frem for Obsero Core, Obsero Enterprise og operationel compliance."
        />
      </Head>

      <PageHero
        narrow
        size="compact"
        title="Kontakt os om demo, scope eller næste skridt."
        copy="Uanset om I vil se løsningen, afklare omfanget eller forstå forskellen mellem Obsero Core og Obsero Enterprise, gør vi næste skridt konkret og relevant for jeres virkelighed."
        actions={[
          { href: "#book-demo", label: "Skriv til os", className: "btn btn-light" },
          { href: "#direkte-kontakt", label: "Ring direkte", className: "btn btn-outline-light" }
        ]}
      />

      <section className="public-section public-section--white-surface">
        <div className="public-container">
          <div className="public-split-callout">
            <div className="public-split-callout__lead public-split-callout__lead--accented">
              <SectionHeading
                eyebrow="Første dialog"
                title="Første samtale skal skabe retning, ikke flere åbne spørgsmål."
                copy="Vi bruger den første dialog på at forstå jeres situation, afklare kravbilledet og vurdere, hvilken vej der giver mest mening hos jer. Målet er en konkret retning, ikke et langt indledende mødeforløb."
              />

              <div className="public-inline-links">
                <SmartLink href={siteLinks.core} className="public-inline-link">
                  Se Obsero Core
                </SmartLink>
                <SmartLink href={siteLinks.enterprise} className="public-inline-link">
                  Se Obsero Enterprise
                </SmartLink>
              </div>
            </div>

            <div className="public-stack-list">
              {meetingAgenda.map((item, index) => (
                <article key={item} className="public-stack-item">
                  <div className="public-stack-item__index">{String(index + 1).padStart(2, "0")}</div>
                  <div className="public-stack-item__body">
                    <p className="public-stack-item__copy mt-0">{item}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="public-section public-section--shell-band" id="book-demo">
        <div className="public-container">
          <SectionHeading
            eyebrow="Kontakt"
            title="Skriv til os, så gør vi næste skridt konkret."
            copy="Fortæl kort, hvor I står, så forbereder vi den rigtige samtale og sørger for, at den rette person følger op."
            className="public-section__header--centered"
          />

          <div className="public-contact-grid public-contact-grid--single">
            <article className="public-contact-card">
              <form
                className="public-contact-form public-contact-form--flush"
                autoComplete="on"
                onSubmit={(event) => {
                  event.preventDefault();
                  transform((values) => ({ contact_request: values }));
                  post(siteLinks.contact, {
                    preserveScroll: true
                  });
                }}
              >
                {flash.notice ? <div className="public-contact-form__success">{flash.notice}</div> : null}

                <div className="public-contact-form__row">
                  <ContactFormField
                    id="contact-name"
                    name="name"
                    label="Navn"
                    type="text"
                    autoComplete="name"
                    value={data.name}
                    onChange={(event) => setData("name", event.target.value)}
                    error={errors.name || serverErrors.name}
                    required
                  />
                  <ContactFormField
                    id="contact-email"
                    name="email"
                    label="E-mail"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={data.email}
                    onChange={(event) => setData("email", event.target.value)}
                    error={errors.email || serverErrors.email}
                    required
                  />
                </div>

                <div className="public-contact-form__row">
                  <ContactFormField
                    id="contact-phone"
                    name="phone"
                    label="Telefon"
                    type="text"
                    autoComplete="tel"
                    inputMode="tel"
                    value={data.phone}
                    onChange={(event) => setData("phone", event.target.value)}
                    error={errors.phone || serverErrors.phone}
                  />
                  <ContactFormField
                    id="contact-organization"
                    name="organization"
                    label="Organisation"
                    type="text"
                    autoComplete="organization"
                    value={data.organization}
                    onChange={(event) => setData("organization", event.target.value)}
                    error={errors.organization || serverErrors.organization}
                  />
                </div>

                <ContactFormField
                  as="select"
                  id="contact-interest"
                  name="interest"
                  label="Jeg vil gerne tale om"
                  autoComplete="off"
                  value={data.interest}
                  onChange={(event) => setData("interest", event.target.value)}
                  error={errors.interest || serverErrors.interest}
                >
                  {interestOptions.map((option) => (
                    <option key={option} value={option}>
                      {contactOptions[option] || option}
                    </option>
                  ))}
                </ContactFormField>

                <ContactFormField
                  as="textarea"
                  id="contact-message"
                  name="message"
                  label="Besked"
                  rows={8}
                  minLength={20}
                  autoComplete="off"
                  value={data.message}
                  onChange={(event) => setData("message", event.target.value)}
                  error={errors.message || serverErrors.message}
                  required
                />

                <div className="public-contact-form__actions">
                  <button type="submit" className="btn btn-primary" disabled={processing}>
                    {processing ? "Sender..." : "Send besked"}
                  </button>
                  <p className="public-contact-form__note">Vi bruger kun jeres oplysninger til at vende tilbage på denne henvendelse.</p>
                </div>
              </form>

            </article>
          </div>
        </div>
      </section>

      <section className="public-section public-section--team-tail">
        <div className="public-container">
          <SectionHeading title="Teamet bag Obsero." className="public-team-intro" />
          <TeamGrid />
        </div>
      </section>

      <CtaBand
        id="direkte-kontakt"
        eyebrow="Direkte kontakt"
        title="Vil I hellere tage den direkte med det samme?"
        copy={`Skriv eller ring direkte, hvis det er nemmere. Obsero A/S · CVR ${companyProfile.cvr} · ${companyProfile.address}, ${companyProfile.postalCity}`}
        actions={[
          { href: companyProfile.emailHref, label: companyProfile.email, className: "btn btn-light" },
          { href: companyProfile.phoneHref, label: companyProfile.phone, className: "btn btn-outline-light" }
        ]}
      />
    </>
  );
}

Contact.layout = (page) => <PublicLayout>{page}</PublicLayout>;

export default Contact;
